import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface BootScreenProps {
  onComplete: () => void;
}

const CAR_WASH_ASCII = `
    ╔══════════════╗
    ║  UNLIMITED   ║
    ║    WASH      ║
    ╠══════════════╣
    ║  ≈≈≈≈≈≈≈≈≈≈  ║
    ║  ≈≈≈≈≈≈≈≈≈≈  ║
    ║  ≈≈≈≈≈≈≈≈≈≈  ║
    ╠══════════════╣
    ║   READY      ║
    ╚══════════════╝
        ║║
    ════╬╬════
        ╚╝
`;

const WASH_ASCII = `
███╗   ███╗ █████╗ ██████╗ ██╗     ███████╗██╗    ██╗ ██████╗  ██████╗ ██████╗
████╗ ████║██╔══██╗██╔══██╗██║     ██╔════╝██║    ██║██╔═══██╗██╔═══██╗██╔══██╗
██╔████╔██║███████║██████╔╝██║     █████╗  ██║ █╗ ██║██║   ██║██║   ██║██║  ██║
██║╚██╔╝██║██╔══██║██╔═══╝ ██║     ██╔══╝  ██║███╗██║██║   ██║██║   ██║██║  ██║
██║ ╚═╝ ██║██║  ██║██║     ███████╗███████╗╚███╔███╔╝╚██████╔╝╚██████╔╝██████╔╝
╚═╝     ╚═╝╚═╝  ╚═╝╚═╝     ╚══════╝╚══════╝ ╚══╝╚══╝  ╚═════╝  ╚═════╝ ╚═════╝
`;

type Phase = 'ibm' | 'nah' | 'wash' | 'logo' | 'loading' | 'complete';

export function BootScreen({ onComplete }: BootScreenProps) {
  const [phase, setPhase] = useState<Phase>('ibm');
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    // Phase 1: IBM logo (2.5s)
    timers.push(setTimeout(() => setPhase('nah'), 2500));

    // Phase 2: "Nah..." (2.5s)
    timers.push(setTimeout(() => setPhase('wash'), 5000));

    // Phase 3: Car wash display (2s)
    timers.push(setTimeout(() => setPhase('logo'), 7000));

    // Phase 4: Logo + loading
    timers.push(setTimeout(() => setPhase('loading'), 8000));

    // Loading progress animation
    const loadingInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(loadingInterval);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 300);
    timers.push(loadingInterval as unknown as NodeJS.Timeout);

    // Complete
    timers.push(setTimeout(() => {
      setPhase('complete');
      setTimeout(onComplete, 500);
    }, 11000));

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [onComplete]);

  return (
    <div className="fixed inset-0 bg-bg-darker flex items-center justify-center overflow-hidden">
      {/* Noise overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-noise" />

      {/* Scanline effect */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)',
        }}
      />

      <AnimatePresence mode="wait">
        {/* Phase 1: IBM Logo */}
        {phase === 'ibm' && (
          <motion.div
            key="ibm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center"
          >
            <div className="text-6xl font-bold text-blue-500 tracking-wider mb-4">
              IBM
            </div>
            <div className="text-text-secondary text-xl tracking-widest">
              Think
            </div>
          </motion.div>
        )}

        {/* Phase 2: Nah... */}
        {phase === 'nah' && (
          <motion.div
            key="nah"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-text-secondary text-2xl mb-4"
            >
              Nah...
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-jrd-orange text-xl"
            >
              Jack made this instead
            </motion.div>
          </motion.div>
        )}

        {/* Phase 3: Car Wash Display */}
        {phase === 'wash' && (
          <motion.div
            key="wash"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-center"
          >
            <pre className="text-jrd-orange text-xs font-mono leading-none">
              {CAR_WASH_ASCII}
            </pre>
          </motion.div>
        )}

        {/* Phase 4: Logo */}
        {(phase === 'logo' || phase === 'loading') && (
          <motion.div
            key="logo"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center"
          >
            {/* Logo image */}
            <motion.img
              src="/maplewood-auto-logo.png"
              alt="Maplewood Auto Service"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="w-32 h-32 mx-auto object-contain mb-4"
            />

            {/* MAPLEWOOD ASCII in green */}
            <motion.pre
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-jrd-green text-[5px] sm:text-[7px] md:text-[8px] font-mono leading-none whitespace-pre mb-2"
            >
              {WASH_ASCII}
            </motion.pre>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-jrd-orange text-sm font-mono tracking-widest mb-8"
            >
              UNLIMITED WASH
            </motion.div>

            {/* Loading bar */}
            {phase === 'loading' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-80 mx-auto"
              >
                <div className="h-2 bg-bg-input rounded-full overflow-hidden border border-text-muted/30">
                  <motion.div
                    className="h-full bg-gradient-to-r from-jrd-orange to-jrd-green"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(loadingProgress, 100)}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <div className="mt-2 text-text-muted text-xs font-mono">
                  <span className="text-jrd-orange">&gt;</span> connecting to wash system...
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* JRD Companies badge */}
      <div
        className="absolute bottom-8 right-8 text-text-muted text-xs font-mono"
        style={{ transform: 'rotate(-15deg)' }}
      >
        <div className="border border-text-muted/30 px-3 py-1 rounded">
          <span className="text-jrd-orange">A</span>{' '}
          <span className="text-text-secondary">JRD Companies</span>{' '}
          <span className="text-jrd-green">Tool</span>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 left-4 text-text-muted text-xs font-mono">
        // crafted with rage by jack
      </div>
    </div>
  );
}
